<template>
  <div v-if="isReadOnly" class="chat-ai-readonly-bar" role="status">
    <span class="chat-ai-readonly-icon">👁</span>
    <span class="chat-ai-readonly-text">
      Mode read-only — Anda melihat percakapan
      <b v-if="readOnlyUserName">{{ readOnlyUserName }}</b><b v-else>user lain</b>.
      Tidak bisa mengirim pesan di sini.
    </span>
  </div>
  <form
    v-else
    class="chat-ai-input-form"
    :class="{ 'chat-ai-input-form-dragover': isDragging }"
    @submit.prevent="onSubmit"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <button
      type="button"
      class="chat-ai-attach-btn"
      @click="onAttachClick"
      :disabled="isStreaming"
      title="Lampirkan file .md sebagai konteks"
      aria-label="Lampirkan file"
    >📎</button>
    <input
      ref="fileInputEl"
      type="file"
      accept=".md,text/markdown,text/plain"
      style="display:none"
      @change="onFileSelected"
    />
    <button
      type="button"
      class="chat-ai-attach-btn"
      @click="onImageClick"
      :disabled="isStreaming"
      title="Lampirkan gambar (PNG/JPEG/WebP/GIF, maks 4 · 5 MB)"
      aria-label="Lampirkan gambar"
    >🖼️</button>
    <input
      ref="imageInputEl"
      type="file"
      accept="image/png,image/jpeg,image/webp,image/gif"
      multiple
      style="display:none"
      @change="onImageSelected"
    />
    <div
      class="chat-ai-input-wrap"
      :class="{ 'chat-ai-input-wrap-focus': inputFocused }"
    >
      <textarea
        ref="inputEl"
        :value="modelValue"
        class="chat-ai-input"
        :placeholder="isStreaming ? 'Ketik pertanyaan berikutnya…' : 'Ketik pertanyaan… (Shift+Enter untuk baris baru, Ctrl+V tempel gambar)'"
        aria-label="Ketik pertanyaan untuk Yustian Smart AI"
        rows="1"
        @input="onInput"
        @focus="inputFocused = true"
        @blur="inputFocused = false"
        @paste="onPaste"
        @keydown.enter.exact.prevent="onSubmit"
        @keydown.shift.enter.exact="$event.stopPropagation()"
        @keydown.esc="$emit('escape')"
      ></textarea>
      <div class="chat-ai-input-meta" v-if="modelValue.length">
        <span class="chat-ai-input-counter" :class="{ 'chat-ai-input-counter-warn': modelValue.length > 1500 }">
          {{ modelValue.length }}
        </span>
      </div>
    </div>
    <button
      v-if="!isStreaming"
      type="submit"
      class="chat-ai-send-btn"
      :disabled="!modelValue.trim()"
      title="Kirim (Enter)"
      aria-label="Kirim pesan"
    >➤</button>
    <button
      v-else
      type="button"
      class="chat-ai-stop-btn"
      @click="$emit('abort')"
      title="Hentikan (Esc)"
      aria-label="Hentikan streaming"
    >■</button>
  </form>
</template>

<script setup>
import { ref, nextTick, watch, onMounted } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  isStreaming: { type: Boolean, default: false },
  // Saat super-admin lihat history user lain — composer di-replace banner.
  isReadOnly: { type: Boolean, default: false },
  readOnlyUserName: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'submit', 'attach', 'image-paste', 'abort', 'escape'])

const inputEl = ref(null)
const fileInputEl = ref(null)
const imageInputEl = ref(null)
const inputFocused = ref(false)
const isDragging = ref(false)
let dragDepth = 0

function autoResize() {
  const el = inputEl.value
  if (!el) return
  el.style.height = 'auto'
  const next = Math.min(el.scrollHeight, 160)
  el.style.height = next + 'px'
}

function onInput(e) {
  emit('update:modelValue', e.target.value)
  nextTick(autoResize)
}

function onSubmit() {
  if (!props.modelValue.trim() || props.isStreaming) return
  emit('submit')
  nextTick(autoResize)
}

function onAttachClick() {
  fileInputEl.value?.click()
}

function onFileSelected(e) {
  const file = e.target.files?.[0]
  e.target.value = ''
  if (file) emit('attach', file)
}

// Image file-picker — routes selected images through the same 'image-paste'
// path as clipboard paste (parent validates type/size per file).
function onImageClick() {
  imageInputEl.value?.click()
}

function onImageSelected(e) {
  const files = Array.from(e.target.files || [])
  e.target.value = ''
  for (const file of files) emit('image-paste', file)
}

// Drag-and-drop images onto the composer. dragenter/dragleave use a depth
// counter so nested children don't flicker the highlight off.
function onDragEnter() {
  dragDepth += 1
  isDragging.value = true
}

function onDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) isDragging.value = false
}

function onDrop(e) {
  dragDepth = 0
  isDragging.value = false
  const files = Array.from(e.dataTransfer?.files || [])
  for (const file of files) {
    if (file.type && file.type.startsWith('image/')) emit('image-paste', file)
  }
}

// Paste handler — picks images out of the clipboard and emits them upward.
// Text paste keeps default browser behavior (insert into textarea).
function onPaste(e) {
  const items = e.clipboardData?.items
  if (!items || !items.length) return
  const imageFiles = []
  for (const item of items) {
    if (item.kind === 'file' && item.type && item.type.startsWith('image/')) {
      const f = item.getAsFile()
      if (f) imageFiles.push(f)
    }
  }
  if (!imageFiles.length) return
  // Prevent the textarea from also receiving raw image bytes as gibberish.
  e.preventDefault()
  for (const file of imageFiles) emit('image-paste', file)
}

function focus() {
  inputEl.value?.focus()
}

watch(() => props.modelValue, () => nextTick(autoResize))

onMounted(() => nextTick(() => { focus(); autoResize() }))

defineExpose({ focus })
</script>

<style scoped>
.chat-ai-readonly-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid rgba(234, 179, 8, 0.35);
  background: linear-gradient(180deg, rgba(234, 179, 8, 0.08), rgba(234, 179, 8, 0.04));
  color: var(--accent-yellow, #eab308);
  font-size: 12px;
  flex-shrink: 0;
  line-height: 1.4;
}
.chat-ai-readonly-bar b { color: var(--text-primary, var(--text-body)); font-weight: 600; }
.chat-ai-readonly-icon { font-size: 14px; flex-shrink: 0; }
.chat-ai-readonly-text { flex: 1; }

.chat-ai-input-form {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--border-soft, var(--border-light));
  background: var(--bg-elev, var(--bg-card));
  flex-shrink: 0;
  align-items: flex-end;
}
.chat-ai-attach-btn {
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  width: 38px;
  height: 40px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s;
}
.chat-ai-attach-btn:hover:not(:disabled) {
  background: var(--bg-hover, var(--bg-hover));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-attach-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.chat-ai-input-form-dragover {
  outline: 2px dashed var(--accent, var(--accent-blue, #3b82f6));
  outline-offset: -5px;
  background: var(--accent-soft, rgba(59, 130, 246, 0.1));
}
.chat-ai-input-form-dragover * { pointer-events: none; }

.chat-ai-input-wrap {
  flex: 1;
  position: relative;
  background: var(--bg-base, var(--bg-deep));
  border: 1px solid var(--border-soft, var(--border-light));
  border-radius: 7px;
  transition: border-color 0.15s, box-shadow 0.15s;
  display: flex;
  align-items: stretch;
}
.chat-ai-input-wrap-focus {
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  box-shadow: 0 0 0 3px var(--accent-soft, rgba(59, 130, 246, 0.18));
}
.chat-ai-input-wrap-busy { opacity: 0.7; }
.chat-ai-input {
  flex: 1;
  background: transparent;
  border: none;
  padding: 9px 12px;
  color: var(--text-primary, var(--text-body));
  font-size: 13px;
  font-family: inherit;
  resize: none;
  outline: none;
  min-height: 40px;
  max-height: 160px;
  line-height: 1.5;
}
.chat-ai-input:disabled { cursor: not-allowed; }
.chat-ai-input::placeholder { color: var(--text-dim, #6b7a8d); }
.chat-ai-input-meta {
  position: absolute;
  right: 8px;
  bottom: 6px;
  pointer-events: none;
}
.chat-ai-input-counter {
  font-size: 10px;
  color: var(--text-dim, #6b7a8d);
  background: var(--bg-elev, var(--bg-card));
  padding: 1px 5px;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
  border: 1px solid var(--border-soft, var(--border-light));
}
.chat-ai-input-counter-warn {
  color: var(--accent-yellow, #eab308);
  border-color: rgba(234, 179, 8, 0.35);
}

.chat-ai-send-btn,
.chat-ai-stop-btn {
  background: var(--accent, var(--accent-blue, #3b82f6));
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px -2px var(--accent-soft, rgba(59, 130, 246, 0.5));
}
.chat-ai-send-btn:hover:not(:disabled) {
  background: var(--accent-hover, var(--accent-blue, #2563eb));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px -2px var(--accent-soft, rgba(59, 130, 246, 0.6));
}
.chat-ai-send-btn:active:not(:disabled) { transform: scale(0.93); }
.chat-ai-send-btn:disabled {
  background: var(--bg-hover, var(--bg-hover));
  color: var(--text-dim, #888);
  cursor: not-allowed;
  box-shadow: none;
}
.chat-ai-stop-btn { background: var(--accent-red, #ef4444); box-shadow: 0 2px 8px -2px rgba(239, 68, 68, 0.5); }
.chat-ai-stop-btn:hover { background: #dc2626; }

@media (max-width: 768px) {
  .chat-ai-input-form {
    padding: 8px 10px;
    gap: 6px;
  }
  .chat-ai-attach-btn {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  .chat-ai-send-btn,
  .chat-ai-stop-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }
  .chat-ai-input {
    padding: 10px 12px;
    font-size: 16px;
    min-height: 44px;
  }
  .chat-ai-readonly-bar {
    padding: 10px 12px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .chat-ai-input-form {
    padding: 6px 8px;
  }
  .chat-ai-attach-btn {
    width: 40px;
    height: 40px;
  }
  .chat-ai-send-btn,
  .chat-ai-stop-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  .chat-ai-readonly-bar {
    padding: 8px 10px;
    font-size: 11px;
    gap: 6px;
  }
}
</style>
